/**
 * Recovery for a phone that died after both tapped (E3b-2a step 9, P2: keep it).
 *
 * On the new design the server writes the room record with both seats at the
 * second tap (055); each phone then writes its own side. A phone that dies in
 * between has a formed room on the server and nothing locally — and neither
 * the People-list orphan check (it looks for a missing host confirm) nor the
 * connection_data top-up (this phone never wrote a row) can see it.
 *
 * This finds those rooms and rebuilds the minimum: the meeting date and the
 * other person's card (first name + photo, from the knock or the host's reply
 * already in the room). Both writes sync to connection_data, so it runs once.
 * What the dead phone chose to share is not rebuilt — it never left the phone.
 * A re-meet needs nothing here: the room both share is already on the phone.
 * New design only — the old design writes its side at the knock.
 */

import { supabase } from '../supabase';
import { loadDataMessages } from '../chat/messageStore';
import { saveConnectionMetadata, saveReceivedAnonData } from '../storage/userDataStorage';
import { isNewExperience } from '../../utils/newExperience';

const CARD_TYPES = ['profile_exchange', 'profile_data_response'];
// Only recent rooms: the gap is a phone that died seconds after both tapped,
// caught on its next open — not a sweep of every connection ever made
const RECENT_DAYS = 30;

/** The other person's latest card in the room, or null. */
export const peerCardOf = (messages, peerProfileUserId) => {
  const cards = messages.filter(
    (m) =>
      CARD_TYPES.includes(m.type) && m.senderProfileUserId === peerProfileUserId && m.profileData
  );
  if (cards.length === 0) return null;
  return { ...cards[cards.length - 1].profileData, senderProfileUserId: peerProfileUserId };
};

/**
 * Rebuild every formed profile-to-profile room this phone holds no record of.
 * @param {string} profileUserId
 * @param {Set<string>} knownIds - rooms already on the phone or in connection_data
 * @returns {Promise<number>} how many rooms were rebuilt
 */
export const recoverFormedRooms = async (profileUserId, knownIds) => {
  if (!isNewExperience()) return 0;
  const since = new Date(Date.now() - RECENT_DAYS * 86400000).toISOString();
  const { data, error } = await supabase
    .from('connections')
    .select('chat_room_id, host_user_id, guest_user_id, host_confirmed_at, guest_confirmed_at')
    .eq('exchange_type', 'pxp')
    .or(`host_user_id.eq.${profileUserId},guest_user_id.eq.${profileUserId}`)
    .not('host_confirmed_at', 'is', null)
    .not('guest_confirmed_at', 'is', null)
    .gt('host_confirmed_at', since)
    .or('is_active.is.null,is_active.eq.true')
    // Per-side delete (CD #15): never resurrect a connection either side left
    .is('host_left_at', null)
    .is('guest_left_at', null);
  if (error || !data) {
    if (error) console.warn('[RECOVERY] Formed-room read failed:', error.message);
    return 0;
  }

  let rebuilt = 0;
  for (const row of data.filter((r) => !knownIds.has(r.chat_room_id))) {
    const room = row.chat_room_id;
    const peer = row.host_user_id === profileUserId ? row.guest_user_id : row.host_user_id;
    const card = peerCardOf(await loadDataMessages(room), peer);
    // No card in the room means nothing to show — leave it rather than a blank person
    if (!card) continue;
    await saveReceivedAnonData(room, card);
    // Both seats are confirmed at the same second tap; either is the meeting
    await saveConnectionMetadata(room, {
      meetingDate: row.host_confirmed_at,
      exchangeType: 'pxp',
    });
    console.log('[RECOVERY] Rebuilt a formed room this phone never recorded:', room);
    rebuilt++;
  }
  return rebuilt;
};
